import { create } from "zustand";
import { persist } from "zustand/middleware";

interface ExploreModelsConfig {
  showCategorySelector: boolean;
  limit: number;
  forceMobileLayout: boolean;
}

interface ExploreModelsStore {
  // State
  activeBrands: string[];
  activeCategory: string;
  activeSubCategory: string | null;
  currentPage: number;
  searchTerm: string;
  sortBy: string;
  config: ExploreModelsConfig;

  // Actions
  setActiveBrands: (brandIds: string[]) => void;
  toggleBrand: (brandId: string) => void;
  setActiveCategory: (categoryId: string) => void;
  setActiveSubCategory: (subCategoryId: string | null) => void;
  setCurrentPage: (page: number) => void;
  setSearchTerm: (term: string) => void;
  setSortBy: (sortBy: string) => void;
  resetPagination: () => void;
  setConfig: (config: Partial<ExploreModelsConfig>) => void;
  reset: () => void;
}

const defaultConfig: ExploreModelsConfig = {
  showCategorySelector: true,
  limit: 6,
  forceMobileLayout: false,
};

const initialState = {
  activeBrands: [] as string[], // Boş = tüm markalar (filtre uygulanmaz)
  activeCategory: "", // İlk kategori otomatik olarak hook tarafından set edilecek
  activeSubCategory: null,
  currentPage: 1,
  searchTerm: "",
  sortBy: "",
  config: defaultConfig,
};

export const useExploreModelsStore = create<ExploreModelsStore>()(
  persist(
    (set) => ({
      ...initialState,

      // Set the full set of active brands
      setActiveBrands: (brandIds: string[]) =>
        set((state) => ({
          activeBrands: brandIds,
          activeSubCategory: null,
          currentPage: state.config.forceMobileLayout ? 1 : state.currentPage,
        })),

      // Toggle a brand in/out of the active set (multi-select)
      toggleBrand: (brandId: string) =>
        set((state) => {
          const isActive = state.activeBrands.includes(brandId);
          const activeBrands = isActive
            ? state.activeBrands.filter((id) => id !== brandId)
            : [...state.activeBrands, brandId];
          return {
            activeBrands,
            // Kategoriyi koru; hook, seçili markalara ait değilse yeniden seçecek
            activeSubCategory: null,
            currentPage: state.config.forceMobileLayout ? 1 : state.currentPage,
          };
        }),

      // Set active category and reset pagination
      setActiveCategory: (categoryId: string) =>
        set((state) => ({
          activeCategory: categoryId,
          activeSubCategory: null, // Reset sub-category when main category changes
          // Pagination'ı resetle (sadece forceMobileLayout varsa)
          currentPage: state.config.forceMobileLayout ? 1 : state.currentPage,
        })),

      // Set active sub-category
      setActiveSubCategory: (subCategoryId: string | null) =>
        set((state) => ({
          activeSubCategory: subCategoryId,
          // Pagination'ı resetle (sadece forceMobileLayout varsa)
          currentPage: state.config.forceMobileLayout ? 1 : state.currentPage,
        })),

      // Set current page
      setCurrentPage: (page: number) => set({ currentPage: page }),

      // Set search term and reset pagination
      setSearchTerm: (term: string) =>
        set({ searchTerm: term, currentPage: 1 }),

      // Set sort by and reset pagination
      setSortBy: (sortBy: string) => set({ sortBy, currentPage: 1 }),

      // Reset pagination to first page
      resetPagination: () => set({ currentPage: 1 }),

      // Update configuration
      setConfig: (config: Partial<ExploreModelsConfig>) =>
        set((state) => ({
          config: { ...state.config, ...config },
        })),

      // Reset to initial state
      reset: () => set(initialState),
    }),
    {
      name: "explore-models-storage",
      version: 3, // Version number for migration
      partialize: (state) => ({
        activeBrands: state.activeBrands,
        activeCategory: state.activeCategory,
        // currentPage persist edilmemeli, her zaman 1'den başlamalı
      }),
      migrate: (persistedState: any, version: number) => {
        // v0/v1/v2 -> v3: çoklu marka seçimine geçildi, seçimleri sıfırla
        if (version < 3) {
          return {
            ...persistedState,
            activeBrands: [],
            activeCategory: "", // İlk kategori otomatik olarak hook tarafından set edilecek
          };
        }
        return persistedState;
      },
    }
  )
);
